import React, {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {getPublicFile} from 'helpers/envHelper'
import {Divider, makeStyles} from '@material-ui/core'
import HealthCenterReportStyle from 'pages/HealthCenter/ReportV1/Style'
import WrapElement from 'pages/HealthCenter/ReportV1/Elements/WrapElement'
import PercentBarCardV2 from 'pages/HealthCenter/ReportV1/Elements/PercentBarCardV2'
import Analysis from 'pages/HealthCenter/ReportV1/Elements/Analysis'
import PercentBarCardV3 from 'pages/HealthCenter/ReportV1/BrainFunctionalConnectivity/Elements/PercentBarCardV3'
import PercentBarCardV4 from 'pages/HealthCenter/ReportV1/BrainFunctionalConnectivity/Elements/PercentBarCardV4'
import useLangEn from 'hooks/useLangEn'
import {ResBrainConnectivity} from 'api/languageApi'
import useLanguage from 'features/languageReport/useLanguage'
import {decodeHTMLEntities, simpleSanitizeHTML} from 'helpers/analysisHelper'

const useStyles = makeStyles(HealthCenterReportStyle)
const getHealthReportReportImage = getPublicFile('images/health_center_report')
export interface BrainFunctionalConnectivityProps {
  partOneImg: string
  brainConnectivityTotalScore: number
  brainConnectivityBetweenLobesScore: {
    vertical: {
      left: number
      right: number
    }
    horizontal: number
  } | null
  brainConnectivityByLobeScore: {
    frontal: {
      left: number
      right: number
    }
    pariocci: {
      left: number
      right: number
    }
  } | null
  pointUnit: string
  textBrainConnectivity: ResBrainConnectivity
  isEEG?: boolean
  scoreAnnotation: string
  footer: string
}

const BrainFunctionalConnectivity = ({
  partOneImg,
  brainConnectivityTotalScore,
  brainConnectivityByLobeScore,
  brainConnectivityBetweenLobesScore,
  textBrainConnectivity,
  isEEG = true,
  scoreAnnotation,
  pointUnit,
  footer,
}: BrainFunctionalConnectivityProps) => {
  const isEn = useLangEn()
  const {t} = useTranslation()
  const classes = useStyles()

  const introData = {...textBrainConnectivity.section.intro}
  const totalData = {...textBrainConnectivity.section.total_connectivity}
  const interData = {...textBrainConnectivity.section.inter_connectivity}
  const intraData = {...textBrainConnectivity.section.intra_connectivity}

  const percentile = textBrainConnectivity.threshold.t_score

  const {healthDifferenceThreshold} = useLanguage(isEEG ? 'eeg' : 'health')

  const getNumber = (value: number | string) => {
    if (!Number.isNaN(Number(value))) return Math.floor(Number(value))
    return 0
  }

  const generateText = (text: string, score: string | number) => {
    return text.replace('{0}', getNumber(score).toString())
  }

  const getSeverity = (value = 0) => {
    if (value < 30) {
      return t('ILow')
    }
    if (value > 70) {
      return t('IHigh')
    }
    return t('ICommon')
  }
  const getText = (value = 0, key: string) => {
    if (value < 30) {
      return t(`IBot${key}`, {
        value: getNumber(value),
      })
    }
    if (value > 70) {
      return t(`ITop${key}`, {
        value: getNumber(value),
      })
    }
    return t(`IMid${key}`, {
      value: getNumber(value),
    })
  }

  const getTextV2 = useCallback(
    (left = 0, right = 0, key = 'Title') => {
      if (left < percentile.low && right < percentile.low) {
        return `IAnalysisResultsDes${key}Case3`
      }
      if (left > percentile.high && right > percentile.high) {
        return `IAnalysisResultsDes${key}Case2`
      }
      if (left > percentile.high && right < percentile.high) {
        return `IAnalysisResultsDes${key}Case4`
      }
      if (left < percentile.high && right > percentile.high) {
        return `IAnalysisResultsDes${key}Case5`
      }
      return `IAnalysisResultsDes${key}Case1`
    },
    [percentile],
  )

  const getTextV3 = useCallback(
    (left = 0, right = 0, key = 'Title') => {
      const average = Math.abs(left - right)

      /// ///  Difference ≤ 10 /// ///
      if (average <= 10) {
        // 좌 OR 우 > 70
        if (left > percentile.high || right > percentile.high) {
          return `IAnalysisResultsDes${key}Case2`
        }

        // 좌 OR 우 < 30
        if (left < percentile.low || right < percentile.low) {
          return `IAnalysisResultsDes${key}Case3`
        }
        // 하기 조건 이외
        return `IAnalysisResultsDes${key}Case1`
      }

      /// /// Difference > 10 /// ///
      // 좌 > 우
      if (left > right) {
        // 좌 and 우 > 70
        if (left > percentile.high && right > percentile.high) {
          return `IAnalysisResultsDes${key}Case2`
        }
        // 하기 조건 이외
        return `IAnalysisResultsDes${key}Case4` // 왼쪽이 크다
      }

      // 좌 < 우
      if (left < right) {
        //  좌 and 우 > 70
        if (left > percentile.high && right > percentile.high) {
          return `IAnalysisResultsDes${key}Case2`
        }
        // 하기 조건 이외
        return `IAnalysisResultsDes${key}Case5` // 오른쪽이 크다
      }

      // 좌 = 우
      return ''
    },
    [percentile],
  )

  const textPart1Title = useMemo(() => {
    if (brainConnectivityTotalScore < percentile.low) {
      return generateText(
        totalData.analysis_results.opinions.low.main,
        brainConnectivityTotalScore,
      )
    }
    if (brainConnectivityTotalScore > percentile.high) {
      return generateText(
        totalData.analysis_results.opinions.high.main,
        brainConnectivityTotalScore,
      )
    }
    return generateText(
      totalData.analysis_results.opinions.normal.main,
      brainConnectivityTotalScore,
    )
  }, [brainConnectivityTotalScore, totalData])

  const textPart1Text = useMemo(() => {
    if (brainConnectivityTotalScore < percentile.low) {
      return totalData.analysis_results.opinions.low.sub
    }
    if (brainConnectivityTotalScore > percentile.high) {
      return totalData.analysis_results.opinions.high.sub
    }
    return totalData.analysis_results.opinions.normal.sub
  }, [brainConnectivityTotalScore, percentile, totalData])

  const replaceMultiText = (text: string, values: (string | number)[]) => {
    return text.replace(/{(\d+)}/g, (_, index) => values[+index].toString())
  }

  const textPart2Title1V1 = useMemo(() => {
    const data = interData?.analysis_results?.opinions?.vertical
    const scoreLeft = getNumber(
      brainConnectivityBetweenLobesScore?.vertical?.left ?? 0,
    )
    const scoreRight = getNumber(
      brainConnectivityBetweenLobesScore?.vertical?.right ?? 0,
    )
    const average = Math.abs(scoreLeft - scoreRight)
    if (average <= healthDifferenceThreshold) {
      if (scoreLeft > percentile.high || scoreRight > percentile.high) {
        return replaceMultiText(data.difference_over.both_high.main, [
          scoreLeft,
          scoreRight,
        ])
      }
      if (scoreLeft < percentile.low || scoreRight < percentile.low) {
        return replaceMultiText(data.difference_under.low.main, [
          scoreLeft,
          scoreRight,
        ])
      }

      return replaceMultiText(data.difference_under.normal.main, [
        scoreLeft,
        scoreRight,
      ])
    }
    if (scoreLeft > scoreRight) {
      if (scoreLeft > percentile.high && scoreRight > percentile.high) {
        return replaceMultiText(data.difference_over.both_high.main, [
          scoreLeft,
          scoreRight,
        ])
      }

      return replaceMultiText(data.difference_over.higher_left.main, [
        scoreLeft,
        scoreRight,
      ])
    }
    if (scoreLeft < scoreRight) {
      if (scoreLeft > 70 && scoreRight > 70) {
        return replaceMultiText(data.difference_over.both_high.main, [
          scoreLeft,
          scoreRight,
        ])
      }

      return replaceMultiText(data.difference_over.higher_right.main, [
        scoreLeft,
        scoreRight,
      ])
    }
    return ''
  }, [brainConnectivityBetweenLobesScore?.vertical])

  const textPart2Title1 = useMemo(() => {
    const key = getTextV3(
      getNumber(brainConnectivityBetweenLobesScore?.vertical?.left ?? 0),
      getNumber(brainConnectivityBetweenLobesScore?.vertical?.right ?? 0),
    )
    return t(key, {
      left: getNumber(brainConnectivityBetweenLobesScore?.vertical?.left ?? 0),
      right: getNumber(
        brainConnectivityBetweenLobesScore?.vertical?.right ?? 0,
      ),
    })
  }, [brainConnectivityBetweenLobesScore?.vertical, isEn])

  const textPart2Text1 = useMemo(() => {
    const data = interData?.analysis_results?.opinions?.vertical
    const scoreLeft = getNumber(
      brainConnectivityBetweenLobesScore?.vertical?.left ?? 0,
    )
    const scoreRight = getNumber(
      brainConnectivityBetweenLobesScore?.vertical?.right ?? 0,
    )
    const average = Math.abs(scoreLeft - scoreRight)
    if (average <= 10) {
      if (scoreLeft > percentile.high || scoreRight > percentile.high) {
        return data.difference_over.both_high.sub
      }
      if (scoreLeft < percentile.low || scoreRight < percentile.low) {
        return data.difference_under.low.sub
      }

      return data.difference_under.normal.sub
    }
    if (scoreLeft > scoreRight) {
      if (scoreLeft > percentile.high && scoreRight > percentile.high) {
        return data.difference_over.both_high.sub
      }

      return data.difference_over.higher_left.sub
    }
    if (scoreLeft < scoreRight) {
      if (scoreLeft > percentile.high && scoreRight > percentile.high) {
        return data.difference_over.both_high.sub
      }

      return data.difference_over.higher_right.sub
    }
    return ''
  }, [brainConnectivityBetweenLobesScore?.vertical, isEn, percentile])

  const textPart2Title2 = useMemo(() => {
    const score = brainConnectivityBetweenLobesScore?.horizontal ?? 0
    const data = interData.analysis_results.opinions.horizontal
    if (score < percentile.low) {
      return data.low.main.replace('{0}', getNumber(score).toString())
    }
    if (score > percentile.high) {
      return data.high.main.replace('{0}', getNumber(score).toString())
    }
    return data.normal.main.replace('{0}', getNumber(score).toString())
  }, [
    brainConnectivityBetweenLobesScore?.horizontal,
    isEn,
    percentile,
    interData,
  ])

  const textPart2Text2 = useMemo(() => {
    const score = brainConnectivityBetweenLobesScore?.horizontal ?? 0
    const data = interData.analysis_results.opinions.horizontal
    if (score < percentile.low) {
      return data.low.sub
    }
    if (score > percentile.high) {
      return data.high.sub
    }
    return data.normal.sub
  }, [
    brainConnectivityBetweenLobesScore?.horizontal,
    isEn,
    percentile,
    interData,
  ])

  const textPart3Title1 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.frontal?.left ?? 0
    const data = intraData.analysis_results.opinions.frontal
    if (score < percentile.low) {
      return data.left.low.main.replace('{0}', getNumber(score).toString())
    }
    if (score > percentile.high) {
      return data.left.high.main.replace('{0}', getNumber(score).toString())
    }
    return data.left.normal.main.replace('{0}', getNumber(score).toString())
  }, [brainConnectivityByLobeScore?.frontal?.left, percentile, intraData])

  const textPart3Text1 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.frontal?.left ?? 0
    const data = intraData.analysis_results.opinions.frontal
    if (score < percentile.low) {
      return data.left.low.sub
    }
    if (score > percentile.high) {
      return data.left.high.sub
    }
    return data.left.normal.sub
  }, [brainConnectivityByLobeScore?.frontal?.left, intraData])

  const textPart3Title2 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.frontal?.right ?? 0
    const data = intraData.analysis_results.opinions.frontal
    if (score < percentile.low) {
      return data.right.low.main.replace('{0}', getNumber(score).toString())
    }
    if (score > percentile.high) {
      return data.right.high.main.replace('{0}', getNumber(score).toString())
    }
    return data.right.normal.main.replace('{0}', getNumber(score).toString())
  }, [brainConnectivityByLobeScore?.frontal?.right, percentile, intraData])

  const textPart3Text2 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.frontal?.right ?? 0
    const data = intraData.analysis_results.opinions.frontal
    if (score < percentile.low) {
      return data.right.low.sub
    }
    if (score > percentile.high) {
      return data.right.high.sub
    }
    return data.right.normal.sub
  }, [brainConnectivityByLobeScore?.frontal?.right, percentile, intraData])

  const textPart3Title3 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.pariocci?.left ?? 0
    const data = intraData.analysis_results.opinions.pariocci
    if (score < percentile.low) {
      return data.left.low.main.replace('{0}', getNumber(score).toString())
    }
    if (score > percentile.high) {
      return data.left.high.main.replace('{0}', getNumber(score).toString())
    }
    return data.left.normal.main.replace('{0}', getNumber(score).toString())
  }, [brainConnectivityByLobeScore?.pariocci?.left, percentile, intraData])

  const textPart3Text3 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.pariocci?.left ?? 0
    const data = intraData.analysis_results.opinions.pariocci
    if (score < percentile.low) {
      return data.left.low.sub
    }
    if (score > percentile.high) {
      return data.left.high.sub
    }
    return data.left.normal.sub
  }, [brainConnectivityByLobeScore?.pariocci?.left, percentile, intraData])

  const textPart3Title4 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.pariocci?.right ?? 0
    const data = intraData.analysis_results.opinions.pariocci
    if (score < percentile.low) {
      return data.right.low.main.replace('{0}', getNumber(score).toString())
    }
    if (score > percentile.high) {
      return data.right.high.main.replace('{0}', getNumber(score).toString())
    }
    return data.right.normal.main.replace('{0}', getNumber(score).toString())
  }, [brainConnectivityByLobeScore?.pariocci?.right, percentile, intraData])

  const textPart3Text4 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.pariocci?.right ?? 0
    const data = intraData.analysis_results.opinions.pariocci
    if (score < percentile.low) {
      return data.right.low.sub
    }
    if (score > percentile.high) {
      return data.right.high.sub
    }
    return data.right.normal.sub
  }, [brainConnectivityByLobeScore?.pariocci?.right, percentile, intraData])
  const showIndex = true

  const getArrShift = () => {
    const list = [...introData.description]
    list.shift()
    list.pop()
    return list
  }
  return (
    <div
      className={classes.brainBasic}
      style={{
        width: '1322px',
      }}
    >
      <div
        className={classes.brainConnectivityTitle}
        // @ts-ignore
        // eslint-disable-next-line
        dangerouslySetInnerHTML={{
          __html: simpleSanitizeHTML(String(introData.description[0])),
        }}
      />

      <div className={classes.brainConnectivityDes}>
        {getArrShift().map((item) => (
          <p
            key={item} // @ts-ignore
            // eslint-disable-next-line
            dangerouslySetInnerHTML={{__html: item}}
          />
        ))}
      </div>
      <div className={classes.brainConnectivityDes} style={{marginBottom: 60}}>
        <p>{introData.description[introData.description.length - 1]}</p>
        <div>
          <ul>
            {textBrainConnectivity.section.intro.text_box.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
        <p>{introData.final_words}</p>
      </div>
      <WrapElement number={1} text={totalData.title}>
        <div className={classes.contentCard}>
          <div className={classes.cardLeftContent}>
            <img
              className={classes.cardLeftImg}
              src={partOneImg}
              alt='Contour'
            />
            <div style={{width: '100%'}}>
              <div style={{height: 24}} />
              <PercentBarCardV2
                index={5}
                headerName={totalData.gauge_bar.title}
                percentage={getNumber(brainConnectivityTotalScore ?? 0)}
                top={totalData.gauge_bar.status.low}
                middle={totalData.gauge_bar.status.normal}
                bottom={totalData.gauge_bar.status.high}
                showIndex={showIndex}
                percentile={percentile}
                pointUnit={pointUnit}
              />
              <div className={classes.scoreAnnotation}>
                {decodeHTMLEntities(scoreAnnotation)}
              </div>
            </div>
          </div>
          <div className={classes.cardRightContent}>
            <p className={classes.textBold}>{totalData.description}</p>
            <Analysis title={totalData.analysis_results.title}>
              <div className={classes.connectivityAnalysis}>
                <p className={classes.textBold}>{textPart1Title}</p>
                <p className={classes.textP}>{textPart1Text}</p>
                <ul>
                  <li>{textPart2Title1V1}</li>
                  <li>{textPart2Title2}</li>
                  <li>{textPart3Title1}</li>
                  <li>{textPart3Title2}</li>
                  <li>{textPart3Title3}</li>
                  <li>{textPart3Title4}</li>
                </ul>
              </div>
            </Analysis>
          </div>
        </div>
      </WrapElement>

      <WrapElement number={2} text={interData.title}>
        <div>
          <div className={classes.brainConnectivityBrainRegionTabs2}>
            <div
              className={`${classes.cardLeftContent} ${classes.cardLeftContentAdditional}`}
            >
              <img
                className={classes.cardLeftImg}
                src={getHealthReportReportImage('brainRegions.webp')}
                alt='Contour'
                style={{width: '285px'}}
              />
            </div>
            <div className={classes.percentBarCardV3}>
              <PercentBarCardV3
                index={1}
                headerName={interData.gauge_bars.vertical.left.title}
                percentage={getNumber(
                  brainConnectivityBetweenLobesScore?.vertical?.left ?? 0,
                )}
                top={interData.gauge_bars.vertical.left.status.high}
                middle={interData.gauge_bars.vertical.left.status.normal}
                bottom={interData.gauge_bars.vertical.left.status.low}
                percentile={percentile}
                pointUnit={pointUnit}
              />
              <PercentBarCardV3
                index={2}
                headerName={interData.gauge_bars.vertical.right.title}
                percentage={getNumber(
                  brainConnectivityBetweenLobesScore?.vertical?.right ?? 0,
                )}
                top={interData.gauge_bars.vertical.right.status.high}
                middle={interData.gauge_bars.vertical.right.status.normal}
                bottom={interData.gauge_bars.vertical.right.status.low}
                percentile={percentile}
                pointUnit={pointUnit}
              />
              <PercentBarCardV3
                index={3}
                headerName={interData.gauge_bars.horizontal.title}
                percentage={getNumber(
                  brainConnectivityBetweenLobesScore?.horizontal ?? 0,
                )}
                top={interData.gauge_bars.horizontal.status.low}
                middle={interData.gauge_bars.horizontal.status.normal}
                bottom={interData.gauge_bars.horizontal.status.high}
                percentile={percentile}
                pointUnit={pointUnit}
              />
            </div>
            <div className={classes.scoreAnnotation}>
              {decodeHTMLEntities(scoreAnnotation)}
            </div>
          </div>
          <div
            className={`${classes.cardRightContent} ${classes.cardRightContentAdditional}`}
          >
            <div>
              <p className={classes.textBold}>{interData.description.main}</p>
              <br />
              <p
                className={classes.textBlack}
                // @ts-ignore
                // eslint-disable-next-line
                dangerouslySetInnerHTML={{
                  __html: simpleSanitizeHTML(interData.description.sub),
                }}
              />
            </div>
            <Analysis title={interData.analysis_results.title}>
              <div className={classes.connectivityBrain}>
                <p className={classes.textBold}>{textPart2Title1V1}</p>
                <p className={classes.textP} style={{marginBottom: 16}}>
                  {textPart2Text1}
                </p>

                <p className={classes.textBold}>{textPart2Title2}</p>
                <p className={classes.textP}>{textPart2Text2}</p>
              </div>
            </Analysis>
          </div>
        </div>
      </WrapElement>
      <WrapElement number={3} text={intraData.title}>
        <div>
          <div className={classes.brainConnectivityBrainRegionTabs3}>
            <div
              className={`${classes.cardLeftContent} ${classes.cardLeftContentAdditionalV1}`}
            >
              <img
                className={classes.cardLeftImg}
                src={getHealthReportReportImage('fourDivisionsBase.webp')}
                alt='Contour'
                style={{width: '285px'}}
              />
            </div>
            <div className={classes.percentBarCar}>
              <PercentBarCardV4
                index={1}
                headerName={intraData.gauge_bars.frontal.left.title}
                percentage={getNumber(
                  brainConnectivityByLobeScore?.frontal?.left ?? 0,
                )}
                top={intraData.gauge_bars.frontal.left.status.low}
                middle={intraData.gauge_bars.frontal.left.status.normal}
                bottom={intraData.gauge_bars.frontal.left.status.high}
                percentile={percentile}
                pointUnit={pointUnit}
              />
              <PercentBarCardV4
                index={3}
                headerName={intraData.gauge_bars.frontal.right.title}
                percentage={getNumber(
                  brainConnectivityByLobeScore?.frontal?.right ?? 0,
                )}
                top={intraData.gauge_bars.frontal.right.status.low}
                middle={intraData.gauge_bars.frontal.right.status.normal}
                bottom={intraData.gauge_bars.frontal.right.status.high}
                percentile={percentile}
                pointUnit={pointUnit}
              />
              <PercentBarCardV4
                index={2}
                headerName={intraData.gauge_bars.pariocci.left.title}
                percentage={getNumber(
                  brainConnectivityByLobeScore?.pariocci?.left ?? 0,
                )}
                top={intraData.gauge_bars.pariocci.left.status.low}
                middle={intraData.gauge_bars.pariocci.left.status.normal}
                bottom={intraData.gauge_bars.pariocci.left.status.high}
                percentile={percentile}
                pointUnit={pointUnit}
              />

              <PercentBarCardV4
                index={4}
                top={intraData.gauge_bars.pariocci.right.status.low}
                headerName={intraData.gauge_bars.pariocci.right.title}
                percentage={getNumber(
                  brainConnectivityByLobeScore?.pariocci?.right ?? 0,
                )}
                middle={intraData.gauge_bars.pariocci.right.status.normal}
                bottom={intraData.gauge_bars.pariocci.right.status.high}
                percentile={percentile}
                pointUnit={pointUnit}
              />
            </div>
            <div className={classes.scoreAnnotation}>
              {decodeHTMLEntities(scoreAnnotation)}
            </div>
          </div>

          <div
            className={`${classes.cardRightContent} ${classes.cardRightContentAdditional}`}
          >
            <div>
              <p className={classes.textBold}>{intraData.description.main}</p>
              <br />
              <p
                className={classes.textBlack}
                // @ts-ignore
                // eslint-disable-next-line
                dangerouslySetInnerHTML={{
                  __html: simpleSanitizeHTML(intraData.description.sub),
                }}
              />
            </div>

            <Analysis title={intraData.analysis_results.title}>
              <div className={classes.connectivityBrain}>
                <p className={classes.textBold}>{textPart3Title1}</p>
                <p className={classes.textP} style={{marginBottom: 16}}>
                  {textPart3Text1}
                </p>

                <p className={classes.textBold}>{textPart3Title2}</p>
                <p className={classes.textP} style={{marginBottom: 16}}>
                  {textPart3Text2}
                </p>

                <p className={classes.textBold}>{textPart3Title3}</p>
                <p className={classes.textP} style={{marginBottom: 16}}>
                  {textPart3Text3}
                </p>

                <p className={classes.textBold}>{textPart3Title4}</p>
                <p className={classes.textP}>{textPart3Text4}</p>
              </div>
            </Analysis>
          </div>
        </div>
      </WrapElement>
      <Divider style={{marginLeft: 10, marginRight: 10, marginBottom: 10}} />
      <div className={classes.footer}>{footer}</div>
    </div>
  )
}

export default BrainFunctionalConnectivity
